import qs from "qs";

import { MediaVariants } from "@is/constants";
import { CacheEntryPriority } from "@is/services/MemoryCache";
import { sortProductMedia } from "@is/utils/content";
import { getEnvValue } from "@is/utils/env";

import { MediaType, MediaVariant, ProductItem } from "../types";
import { getMainImageUrl } from "./content";

export const isImage = (media: MediaType): boolean | null =>
  media.contentType ? media.contentType.startsWith("image") : null;

export const isVideo = (media: MediaType): boolean | null =>
  media.contentType ? media.contentType.startsWith("video") : null;

export function getImageUri(
  imageServiceUrl: string,
  url: string,
  options: MediaVariant
) {
  const queryString = qs.stringify({
    url: encodeURI(url),
    height: options.height,
    width: options.width,
    background: options.background,
    format: options.format ?? "webp",
    quality: options.quality ?? 80,
    inputSpeed: options.inputSpeed,
    outputFps: options.outputFps,
    animated: options.animated ?? true,
    seekMs: options.seekMs,
    removeBg: options.removeBg,
    smartCrop: options.smartCrop,
  });

  return `${imageServiceUrl}?${queryString}`;
}

export function getVideoUri(
  videoServiceUrl: string,
  url: string,
  options: MediaVariant
) {
  const queryString = qs.stringify({
    url: encodeURI(url),
    height: options.height,
    width: options.width,
  });

  return `${videoServiceUrl}/?${queryString}`;
}

const getImageResolution = (image, url) => {
  return new Promise<{
    width: number;
    height: number;
  }>((resolve) => {
    let img = image;
    if (!img) {
      img = new Image();
    }

    const mediaServiceUri = getImageUri(
      getEnvValue("VITE_IMAGE_SERVICE_URL"),
      url,
      MediaVariants.LARGE_CROPPED_WITH_BACKGROUND_REMOVED
    );

    img.src = mediaServiceUri;

    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
  });
};

export async function getReducedImageResolution(
  url: string,
  maxReducedWidth?: number,
  maxReducedHeight?: number
) {
  const img = new Image();

  const result = await getImageResolution(img, url);
  const { width, height } = result;

  const aspectRatio = width / height;

  let reducedWidth = width;
  let reducedHeight = height;

  if (maxReducedWidth) {
    const widthConstrainedHeight = Math.round(maxReducedWidth / aspectRatio);
    if (maxReducedWidth < width) {
      reducedWidth = maxReducedWidth;
      reducedHeight = widthConstrainedHeight;
    }
  }

  if (maxReducedHeight) {
    const heightConstrainedWidth = Math.round(maxReducedHeight * aspectRatio);
    if (maxReducedHeight < height && heightConstrainedWidth < reducedWidth) {
      reducedHeight = maxReducedHeight;
      reducedWidth = heightConstrainedWidth;
    }
  }

  return {
    ...result,
    width: reducedWidth,
    height: reducedHeight,
  };
}

export function getContentPreloadMedia(items: ProductItem[]) {
  const mainContentMedia = [];
  const otherMainColorMedia = [];

  items.forEach((item) => {
    const mainImage = getMainImageUrl(item.media);
    if (mainImage) {
      mainContentMedia.push({
        url: getImageUri(
          getEnvValue("VITE_IMAGE_SERVICE_URL"),
          mainImage,
          MediaVariants.MEDIUM
        ),
        cachePriority: CacheEntryPriority.NEVER_REMOVE,
      });
    }

    const media = sortProductMedia(item.media);
    media.forEach((m) => {
      if (m.url !== mainImage) {
        otherMainColorMedia.push({
          url: getImageUri(
            getEnvValue("VITE_IMAGE_SERVICE_URL"),
            m.url,
            MediaVariants.MEDIUM
          ),
          cachePriority: CacheEntryPriority.NEVER_REMOVE,
        });
      }
    });
  });

  return [...mainContentMedia, ...otherMainColorMedia];
}
